import { useEffect, useState } from "react";
import { FlaskConical } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { MobileNav } from "@/components/layout/MobileNav";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { SHELL } from "@/constants/testIds";

const COLLAPSE_KEY = "rsid_sidebar_collapsed";

/**
 * Authenticated AgentHQ application shell: collapsible navy sidebar + persistent
 * top bar + main workspace + mobile bottom navigation. Responsive across
 * desktop / tablet / mobile. Collapse preference persists via localStorage.
 * (CRM action dialogs live in CrmActionsProvider, mounted around <Routes> in App.js.)
 */
export const AppShell = ({ children }) => {
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const { workspace } = useAuth();
  const isDemo = Boolean(workspace?.is_demo);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  return (
    <div className="flex min-h-screen bg-secondary/40" data-testid={SHELL.root}>
      <a href="#main-content" className="skip-link" data-testid={SHELL.skipLink}>
        Skip to content
      </a>

      <Sidebar
        open={navOpen}
        onClose={() => setNavOpen(false)}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onMenuClick={() => setNavOpen(true)} />
        {isDemo && (
          <div
            data-testid="demo-workspace-banner"
            className="flex items-center justify-center gap-2 bg-brand-gold/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-brand-navy"
          >
            <FlaskConical className="h-3.5 w-3.5" />
            Demo Workspace · fictional data for demonstration
          </div>
        )}
        <main
          id="main-content"
          data-testid={SHELL.content}
          className="flex-1 px-4 py-6 pb-24 sm:px-6 lg:px-10 lg:py-10 lg:pb-10"
        >
          <div className="mx-auto w-full max-w-6xl animate-fade-in-up">
            <ErrorBoundary>{children}</ErrorBoundary>
          </div>
        </main>
      </div>

      <MobileNav />
    </div>
  );
};
